import { More } from "components/field/radarUi";
import { finiteNumber, formatCm, formatSignedCm, levelSourceText } from "utils/radarLab";

const cm = (value) => (finiteNumber(value) === null ? "—" : formatCm(value, 2));

// A lab run's own ground truth: for every settled step, the ruler's change of
// level against ours and the radar's own, and the pump's factor by the ruler.
export function RunGroundTruth({ run }) {
  const truth = run?.ruler;
  if (!truth || !(truth.photos || []).length) return null;
  const summary = truth.summary || {};
  const factor = summary.ruler_factor || {};
  const refined = run?.calibration?.refined || {};
  const factorText = (direction) =>
    finiteNumber(factor[direction]) !== null
      ? `${factor[direction]}${finiteNumber(refined[direction]) !== null ? ` (ours ${refined[direction]})` : ""}`
      : null;
  const factors = ["fill", "drain"]
    .map((d) => (factorText(d) ? `${d} ${factorText(d)}` : null))
    .filter(Boolean);
  return (
    <div className="field-lab-truth-run" aria-label="Ground truth of this lab run">
      <h3 className="field-radar-heading">Ground truth (ruler camera)</h3>
      <dl className="field-lab-facts">
        <div>
          <dt>Photos read</dt>
          <dd>
            {truth.photos_read ?? 0} of {truth.photos.length} (reader {truth.reader_version})
          </dd>
        </div>
        <div>
          <dt>Steps compared</dt>
          <dd>
            {summary.compared ?? 0} of {summary.settled_steps ?? 0} settled
          </dd>
        </div>
        <div>
          <dt>Our change vs the ruler's</dt>
          <dd>
            {summary.ours ? `median ${cm(summary.ours.median_cm)}, largest ${cm(summary.ours.max_cm)}` : "—"}
          </dd>
        </div>
        <div>
          <dt>Radar's own change vs the ruler's</dt>
          <dd>
            {summary.radar ? `median ${cm(summary.radar.median_cm)}, largest ${cm(summary.radar.max_cm)}` : "—"}
          </dd>
        </div>
        <div>
          <dt>Pump factor by the ruler</dt>
          <dd>{factors.length ? factors.join(" · ") : "—"}</dd>
        </div>
        <div>
          <dt>Our estimate vs the ruler</dt>
          <dd>{finiteNumber(summary.level_vs_ruler_cm) !== null ? formatSignedCm(summary.level_vs_ruler_cm, 2) : "—"}</dd>
        </div>
      </dl>
      {(truth.steps || []).length > 0 && (
        <More label={`Each step against the ruler (${truth.steps.length})`}>
          <div className="field-lab-scroll">
            <table className="field-table field-lab-truth" aria-label="Step changes against the ruler">
              <thead>
                <tr>
                  <th scope="col">Step</th>
                  <th scope="col">Ruler</th>
                  <th scope="col">Ours</th>
                  <th scope="col">Radar's own</th>
                  <th scope="col">Level decided by</th>
                </tr>
              </thead>
              <tbody>
                {truth.steps.map((row) => (
                  <tr key={row.seq}>
                    <td>
                      {row.seq + 1} · {formatSignedCm(row.commanded_cm)}
                    </td>
                    <td>
                      {row.ruler_change_cm !== null && row.ruler_change_cm !== undefined
                        ? formatSignedCm(row.ruler_change_cm, 2)
                        : "—"}
                      {finiteNumber(row.ruler_ratio) !== null && <small>{row.ruler_ratio} × commanded</small>}
                    </td>
                    <td>
                      {finiteNumber(row.our_change_cm) !== null ? formatSignedCm(row.our_change_cm, 2) : "—"}
                      {finiteNumber(row.our_error_cm) !== null && <small>{formatSignedCm(row.our_error_cm, 2)} off</small>}
                    </td>
                    <td>
                      {finiteNumber(row.radar_change_cm) !== null ? formatSignedCm(row.radar_change_cm, 2) : "—"}
                      {finiteNumber(row.radar_error_cm) !== null && (
                        <small>{formatSignedCm(row.radar_error_cm, 2)} off</small>
                      )}
                    </td>
                    <td>{levelSourceText(row) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </More>
      )}
    </div>
  );
}
